import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import ReactGA from "react-ga";
import { sendPageView } from "lib/googleAnalytics/page";
import log from "loglevel";
import { Container, Row, Col, Card, Modal, Alert } from "react-bootstrap";
import { Navbar } from "@avlino-inc/alenza-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PasswordStrengthMeter from "components/blocks/PasswordStrengthMeter";
import {
  createPasswordNoAuthAction,
  resetPhoneAction,
} from "store/actions/actionCreators";
import {
  getCreatePasswordNoAuthReducerResponse,
  getCreatePasswordNoAuthReducerError,
} from "store/selectors";
import {
  isStringValid,
  validPassword,
  getInputValidity,
} from "util/formValidators";
import genStyles from "styles/General.module.scss";
import styles from "./Auth.module.scss";
import { passwordRequirements } from "util/reactSnippets";
import { getTheme } from "lib/theme";
import { ThemeLogo } from "components/pages/auth/ThemeLogo";
import {
  CreatePasswordNoAuthRequestType,
  ResetPhoneRequestType,
  SuccessType,
  ErrorType,
} from "types";
import * as routes from "routes";
import { Form } from "@avlino-inc/alenza-react";

type CreatePasswordFormType = {
  new_password: string | undefined;
  confirm_password: string | undefined;
  token: string | undefined;
};

const defaultCreatePassword = {
  new_password: undefined,
  confirm_password: undefined,
  token: undefined,
};

type ExpectedUrlParams = {
  verificationToken?: string;
};

const AuthResetPage = ({ authed }: { authed: Promise<boolean> }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const params: ExpectedUrlParams = useParams();

  const createPasswordNoAuthResponse: SuccessType = useSelector((state) =>
    getCreatePasswordNoAuthReducerResponse(state, "data")
  );
  const createPasswordNoAuthError: ErrorType = useSelector((state) =>
    getCreatePasswordNoAuthReducerError(state)
  );

  const [authorized, setAuthorized] = useState<boolean>(false);
  const [createPassword, setCreatePassword] = useState<CreatePasswordFormType>(
    defaultCreatePassword
  );
  const [deactivatingPhone, setDeactivatingPhone] = useState<boolean>(false);
  const [isChangingPassword, setIsChangingPassword] = useState<boolean>(false);
  const [displayMessage, setDisplayMessage] = useState<JSX.Element>();
  const [isDisplayingMessage, setIsDisplayingMessage] = useState<boolean>();
  const [showPasswordModal, setShowPasswordModal] = useState<boolean>(false);
  const [theme, setTheme] = useState<string | undefined>();

  const passwordRequirementsModal = () => {
    return (
      <>
        <Modal
          aria-labelledby="contained-modal-title-vcenter"
          centered
          show={showPasswordModal}
          onHide={() => setShowPasswordModal(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Password Requirements{" "}
              <FontAwesomeIcon icon={["far", "info-circle"]} />
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>{passwordRequirements()}</Modal.Body>
        </Modal>
      </>
    );
  };

  const handleView = () => {
    let view;
    if (location.pathname.includes("verify-email")) {
      view = "password";
    } else if (location.pathname.includes("reset-password")) {
      view = "password";
    } else if (location.pathname.includes("reset-phone")) {
      view = "phone";
    }
    return view;
  };

  const displayPhoneView = () => {
    return (
      <>
        <p>{displayMessage}</p>
        <input
          type="submit"
          value="Register"
          className={[genStyles["alenza__btn-primary"], "w-75"].join(" ")}
          onClick={() => {
            history.push(routes.TWOFACTOR_REGISTER);
          }}
        />
      </>
    );
  };

  const onHandleResetPasswordRequest = (event: any) => {
    event.preventDefault();
    if (
      createPassword?.new_password &&
      createPassword?.confirm_password &&
      createPassword?.token
    ) {
      log.info("All fields are valid! Creating/Resetting Password ...");
      const request: CreatePasswordNoAuthRequestType = {
        body: {
          new_password: createPassword.new_password,
          confirm_password: createPassword.confirm_password,
          token: createPassword.token,
        },
      };
      log.info("-- Dispatching createPasswordNoAuthAction()...");
      dispatch(createPasswordNoAuthAction(request));
      setIsChangingPassword(false);
    } else {
      log.info(
        "One or more fields NOT VALID! Will not create/reset password. " +
          JSON.stringify(createPassword)
      );
    }
  };

  const onClickUserCard = useCallback(() => history.push(routes.LOGIN), []);

  useEffect(() => {
    if (authed) {
      authed.then((allow) => {
        log.info("Allow: " + allow);
        if (allow) {
          setAuthorized(true);
        } else {
          history.push(routes.LOGIN);
        }
      });
    } else {
      history.push(routes.LOGIN);
    }
  }, [authed, history]);

  useEffect(() => {
    !theme ? setTheme(getTheme()) : log.info("Theme already set.");
    if (
      params?.verificationToken &&
      location.pathname.startsWith(routes.RESET_PHONE)
    ) {
      if (params.verificationToken) {
        log.info("Deactivating Phone...");
        const request: ResetPhoneRequestType = {
          body: {
            token: params.verificationToken,
          },
        };
        dispatch(resetPhoneAction(request));
      } else {
        log.warn("Verification Token param not provided");
      }
    } else {
      // Only sets once
      if (params && params.verificationToken && !createPassword?.token) {
        setCreatePassword({
          new_password: createPassword?.new_password,
          confirm_password: createPassword?.new_password,
          token: params.verificationToken,
        });
        log.info(
          "Token attached to CreatePassword: " + JSON.stringify(createPassword)
        );
      }
    }
  }, [dispatch, location.pathname, createPassword, theme, params]);

  useEffect(() => {
    if (
      !isDisplayingMessage &&
      createPasswordNoAuthError &&
      createPasswordNoAuthError.message
    ) {
      setIsDisplayingMessage(true);
      setDisplayMessage(
        <Alert variant="danger">{createPasswordNoAuthError.message}</Alert>
      );
    } else if (
      createPasswordNoAuthResponse &&
      createPasswordNoAuthResponse.success
    ) {
      log.info("Successful Password Change!");
      history.push(routes.LOGIN);
    }
  }, [
    createPasswordNoAuthError,
    isDisplayingMessage,
    createPasswordNoAuthResponse,
    history,
  ]);

    /**
   * Google Analytics
   */
  useEffect(() => {
    sendPageView(location.pathname + location.search);
  }, [location]);

  return (
    <div id="AuthLoginPage" className={styles["auth-page"]}>
      <Navbar
					showLogo
					logoUrl='/'
          productDefinition = {
            {
              productName:'Alenza'
            }
          }
					productNameUrl='/'
					dropdown={undefined}
					notificationItems={undefined}
          onClickUserCard={onClickUserCard}
          email={""}
      />
      <Container className={styles["auth-page__container"]} fluid={true}>
        <Row className={styles["auth-page__container__row"]}>
          <Col md={12} className={styles["auth-page__container__row__content"]}>
            <Card
              className={
                styles["auth-page__container__row__content__auth-card"]
              }
            >
              <Card.Body className="text-center">
                <div>
                  <ThemeLogo theme={theme} />
                </div>
                <div>
                  {handleView() === "password" ? (
                    <>
                      <Card.Title>Change Password</Card.Title>
                      <div style={{ marginTop: "10px", color: "red" }}>
                        {isDisplayingMessage ? displayMessage : null}
                      </div>
                      <form
                        name="create-user-form"
                        onSubmit={onHandleResetPasswordRequest}
                      >
                        <div className="text-center w-100">
                          <div className="meter">
                            <div style={{ marginBottom: "15px" }}>
                              <Form.Group controlId="new_password">
                                <Form.Input
                                  type="password"
                                  placeholder="New Password"
                                  status={getInputValidity(
                                    createPassword?.new_password,
                                    validPassword
                                  )}
                                  onChange={(e: any) => {
                                    if (e.target instanceof HTMLInputElement) {
                                      const { value } = e.target;
                                      setCreatePassword(
                                        (state): typeof state => ({
                                          ...state,
                                          new_password: value,
                                        })
                                      );
                                    }
                                  }}
                                />
                              </Form.Group>
                            </div>
                            <div style={{ marginBottom: "15px" }}>
                              <Form.Group controlId="confirm_password">
                                <Form.Input
                                  type="password"
                                  placeholder="Confirm Password"
                                  status={
                                    createPassword &&
                                    ![undefined, ""].includes(
                                      createPassword.confirm_password
                                    )
                                      ? "valid"
                                      : undefined
                                  }
                                  onChange={(e: any) => {
                                    if (e.target instanceof HTMLInputElement) {
                                      const value = e.target.value;
                                      setCreatePassword(
                                        (state): typeof state => ({
                                          ...state,
                                          confirm_password: value,
                                        })
                                      );
                                      setIsDisplayingMessage(false);
                                    }
                                  }}
                                />
                              </Form.Group>
                            </div>
                            <PasswordStrengthMeter
                              password={createPassword?.new_password}
                            />
                            {passwordRequirementsModal()}
                            <div style={{ marginBottom: "15px" }}>
                              <div
                                className={genStyles["link"]}
                                onClick={(e) => setShowPasswordModal(true)}
                              >
                                Show Password Requirements
                              </div>
                            </div>
                          </div>
                          <input
                            type="submit"
                            value="Save & Verify"
                            className={[
                              genStyles["alenza__btn-primary"],
                              "w-75",
                            ].join(" ")}
                            disabled={
                              !validPassword(createPassword?.new_password) ||
                              !createPassword?.confirm_password
                            }
                            onClick={(e: any) => {
                              onHandleResetPasswordRequest(e);
                            }}
                          />
                        </div>
                      </form>
                    </>
                  ) : (
                    displayPhoneView()
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AuthResetPage;
